import React from 'react';
import {
  Box, Stack, TextField, Typography,
} from '@mui/material';
import {
  PlanNetwork, networkLabel, technologiesFor,
} from '../features/plans/planNetwork';

export interface PlanWholesaleIdsProps {
  network: PlanNetwork;
  /** The Layer 3 product ids: what an order on MEF, the default surface, names. */
  externalPlanIds: Record<string, string>;
  /** The Layer 2 product ids: what an order on the NPIS surface names. */
  layer2PlanIds?: Record<string, string>;
  onChange: (externalPlanIds: Record<string, string>, layer2PlanIds: Record<string, string>) => void;
}

/**
 * The wholesaler's product ids for this plan on each access technology, one
 * set per ordering surface. Nothing to show for a network Pineapple owns.
 */
const PlanWholesaleIds = ({
  network, externalPlanIds, layer2PlanIds = {}, onChange,
}: PlanWholesaleIdsProps) => {
  const technologies = technologiesFor(network);
  if (technologies.length === 0) return null;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2">{`${networkLabel(network)} Layer 3 product ids (MEF)`}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          The product this plan is ordered as on the usual (MEF) surface, on each
          access technology in the network. Leave a technology empty when the
          plan is not sold on it, and it is then not offered at addresses served by it.
        </Typography>
        <Stack spacing={2}>
          {technologies.map((t) => (
            <TextField
              key={t.key}
              label={t.label}
              value={externalPlanIds[t.key] ?? ''}
              placeholder={t.placeholder}
              onChange={(e) => onChange({ ...externalPlanIds, [t.key]: e.target.value }, layer2PlanIds)}
              fullWidth
            />
          ))}
        </Stack>
      </Box>
      <Box>
        <Typography variant="subtitle2">{`${networkLabel(network)} Layer 2 product ids (NPIS)`}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          The product the plan is ordered as when the sign up asks for the NPIS
          surface instead. Only needed while that switch exists.
        </Typography>
        <Stack spacing={2}>
          {technologies.map((t) => (
            <TextField
              key={`layer2-${t.key}`}
              label={`${t.label} (layer 2)`}
              value={layer2PlanIds[t.key] ?? ''}
              placeholder={t.placeholder}
              onChange={(e) => onChange(externalPlanIds, { ...layer2PlanIds, [t.key]: e.target.value })}
              fullWidth
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default PlanWholesaleIds;
